import { CreateTemplateRequest } from './whatsapp.model';

/**
 * Plantillas de arranque para un negocio que acaba de conectar WhatsApp.
 *
 * Sin al menos una plantilla APROBADA no se puede escribir primero a nadie ni responder
 * fuera de la ventana de 24 h, así que una cuenta recién conectada queda muda hasta que
 * alguien redacta una y Meta la aprueba. Estas son el punto de partida: se dan de alta
 * con un clic desde Plantillas de WhatsApp y quedan en revisión de Meta.
 *
 * Criterios con los que están escritas, que son los que Meta mira al revisarlas:
 * - UTILITY = mensaje sobre algo que el cliente ya tiene abierto (su consulta, su reunión,
 *   su cotización, su caso). MARKETING = promoción o primer contacto; cuesta más y exige
 *   consentimiento previo y salida ("responde BAJA").
 * - Nada de texto que prometa o venda dentro de una UTILITY: Meta la recategoriza o la rechaza.
 * - Los {{n}} van numerados y con un ejemplo real cada uno; sin ejemplos Meta rechaza el alta.
 */
export interface WhatsappTemplateSuggestion {
  /** Título en la tarjeta. */
  title: string;
  /** Cuándo conviene usarla. */
  description: string;
  /** Qué representa cada {{n}}, en orden. Se pinta bajo el cuerpo. */
  paramLabels: string[];
  template: CreateTemplateRequest;
}

export const WHATSAPP_TEMPLATE_SUGGESTIONS: readonly WhatsappTemplateSuggestion[] = [
  {
    title: 'Retomar una consulta',
    description:
      'La más útil de todas: reabre la conversación cuando pasaron más de 24 h desde el ' +
      'último mensaje del cliente. Si vas a crear una sola, que sea esta.',
    paramLabels: ['Nombre del contacto', 'Nombre de tu empresa'],
    template: {
      name: 'seguimiento_consulta',
      language: 'es',
      category: 'UTILITY',
      bodyText:
        'Hola {{1}}, te escribimos de {{2}} para dar seguimiento a tu consulta. ' +
        'Si quieres retomarla, responde este mensaje y un asesor te atiende.',
      bodyExamples: ['María', 'Partner Tech'],
      footerText: 'Responde para continuar la conversación.',
    },
  },
  {
    title: 'Primer contacto con un lead',
    description:
      'Para escribirle por primera vez a alguien que dejó sus datos en un formulario, ' +
      'la web o una campaña. Es MARKETING: Meta exige consentimiento previo y salida.',
    paramLabels: ['Nombre del contacto', 'Dónde dejó sus datos', 'Nombre del asesor'],
    template: {
      name: 'bienvenida_contacto',
      language: 'es',
      category: 'MARKETING',
      bodyText:
        'Hola {{1}}, gracias por dejarnos tus datos en {{2}}. Soy {{3}} y te acompaño ' +
        'desde aquí con lo que necesites.',
      bodyExamples: ['María', 'nuestro formulario web', 'Jorge'],
      footerText: 'Responde BAJA para no recibir más mensajes.',
    },
  },
  {
    title: 'Confirmación de reunión',
    description: 'Se envía al agendar. Deja por escrito la fecha y la hora acordadas.',
    paramLabels: ['Nombre del contacto', 'Motivo de la reunión', 'Fecha', 'Hora'],
    template: {
      name: 'confirmacion_reunion',
      language: 'es',
      category: 'UTILITY',
      headerText: 'Reunión agendada',
      bodyText:
        'Hola {{1}}, tu reunión de {{2}} quedó agendada para el {{3}} a las {{4}}. ' +
        'Si necesitas moverla, responde este mensaje.',
      bodyExamples: ['María', 'asesoría comercial', 'lunes 8 de septiembre', '15:00'],
    },
  },
  {
    title: 'Recordatorio de reunión',
    description: 'Para el aviso previo, unas horas o un día antes de la cita.',
    paramLabels: ['Nombre del contacto', 'Motivo de la reunión', 'Fecha', 'Hora'],
    template: {
      name: 'recordatorio_reunion',
      language: 'es',
      category: 'UTILITY',
      headerText: 'Recordatorio',
      bodyText:
        'Hola {{1}}, te recordamos tu reunión de {{2}}: {{3}} a las {{4}}. ' +
        'Si no puedes asistir, responde este mensaje y la reprogramamos.',
      bodyExamples: ['María', 'asesoría comercial', 'mañana martes 9', '15:00'],
      footerText: 'Te esperamos.',
    },
  },
  {
    title: 'Aviso de cotización enviada',
    description: 'Avisa por WhatsApp que la cotización ya salió, con su número y su monto.',
    paramLabels: ['Nombre del contacto', 'Número de cotización', 'Monto total'],
    template: {
      name: 'cotizacion_enviada',
      language: 'es',
      category: 'UTILITY',
      bodyText:
        'Hola {{1}}, ya te enviamos la cotización {{2}} por {{3}}. ' +
        'Si tienes dudas, respóndenos por aquí.',
      bodyExamples: ['María', 'COT-2026-0125', 'S/ 4,500.00'],
    },
  },
  {
    title: 'Seguimiento de cotización',
    description: 'Para el contacto que recibió una cotización y todavía no responde.',
    paramLabels: ['Nombre del contacto', 'Número de cotización', 'Fecha de vencimiento'],
    template: {
      name: 'seguimiento_cotizacion',
      language: 'es',
      category: 'UTILITY',
      bodyText:
        'Hola {{1}}, tu cotización {{2}} está vigente hasta el {{3}}. ' +
        '¿La revisamos juntos antes de esa fecha?',
      bodyExamples: ['María', 'COT-2026-0125', '15 de septiembre'],
    },
  },
  {
    title: 'Caso registrado',
    description: 'Acuse de recibo de un ticket de soporte, con su código.',
    paramLabels: ['Nombre del contacto', 'Código del ticket'],
    template: {
      name: 'ticket_recibido',
      language: 'es',
      category: 'UTILITY',
      headerText: 'Caso registrado',
      bodyText:
        'Hola {{1}}, registramos tu caso {{2}}. Un asesor lo está revisando y ' +
        'te escribimos por aquí en cuanto haya novedades.',
      bodyExamples: ['María', 'TKT-2026-000420'],
    },
  },
  {
    title: 'Caso resuelto',
    description: 'Cierre de un ticket, dejando abierta la puerta a reabrirlo.',
    paramLabels: ['Nombre del contacto', 'Código del ticket'],
    template: {
      name: 'ticket_resuelto',
      language: 'es',
      category: 'UTILITY',
      bodyText:
        'Hola {{1}}, tu caso {{2}} quedó resuelto. Si el problema continúa, ' +
        'responde este mensaje y lo reabrimos.',
      bodyExamples: ['María', 'TKT-2026-000420'],
      footerText: 'Gracias por escribirnos.',
    },
  },
  {
    title: 'Promoción de temporada',
    description:
      'Envío comercial a una lista de contactos. MARKETING: exige consentimiento previo ' +
      'y una salida visible en el pie.',
    paramLabels: ['Nombre del contacto', 'Qué se ofrece', 'Hasta cuándo'],
    template: {
      name: 'promocion_temporada',
      language: 'es',
      category: 'MARKETING',
      bodyText:
        'Hola {{1}}, tenemos {{2}} hasta el {{3}}. Responde este mensaje y te damos ' +
        'todos los detalles.',
      bodyExamples: ['María', '20% de descuento en el plan anual', '30 de septiembre'],
      footerText: 'Responde BAJA para no recibir más mensajes.',
    },
  },
];
